import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrency } from "./CurrencyContext";
import ShippingAddress from "../shipment/ShippingAddress";
import Modal from "./Modal";

const BasketTotals = ({
  calculateItemPrice,
  cart,
  currencySymbol,
  subtotal,
}) => {
  const navigate = useNavigate();
  const { currency, getCurrencySymbol } = useCurrency();
  const [showModal, setShowModal] = useState(false);
  const [address, setAddress] = useState({
    country: "",
    state: "",
    town: "",
    pin_code: "",
  });

  const vat = subtotal * 0.18;

  // IMPORTANT: keys must match the `currency` value from CurrencyContext.
  // Your Table2 dropdown uses: RUPEE | DOLLAR | EURO
  const minimumOrders = {
    RUPEE: 500, // ₹500
    EURO: 5,    // €5
    DOLLAR: 6,  // $6
  };

  const requiredMinimum = minimumOrders[currency] || 6;
  const isBelowMinimum = subtotal < requiredMinimum;
  const minimumOrderAdjustment = isBelowMinimum
    ? requiredMinimum - subtotal
    : 0;
  const total = subtotal + vat + minimumOrderAdjustment;

  const handleProceedToCheckout = () => {
    if (currency === "RUPEE" && total >= 100000) {
      setShowModal(true);
      return;
    }

    const shippingAddress = {
      country: address.country || "0",
      state: address.state || "0",
      town: address.town || "0",
      pin_code: address.pin_code || "0",
    };

    localStorage.setItem("shippingAddress", JSON.stringify(shippingAddress));

    navigate("/checkout", {
      state: {
        cart: cart.map((item) => ({ ...item })),  // spread keeps all tiers
        shippingAddress,
        total,
        vat,
      },
    });
  };

  return (
    <>
      <form className="border border-gray-300 bg-gray-50 p-6 shadow-sm">
        <div className="space-y-12">
          <div className="border-b border-gray-900/10 pb-12">
            <h1 className="text-2xl font-bold">Basket totals</h1>

            <div className="mt-6">
              <h2 className="text-lg font-semibold text-gray-800 text-left">
                Your Items
              </h2>
              {cart.length > 0 ? (
                <ul className="mt-4 space-y-2 text-left">
                  {cart.map((item) => {
                    const unit = calculateItemPrice
                      ? calculateItemPrice(item.quantity, item)
                      : 0;
                    const lineTotal = unit * item.quantity;
                    return (
                      <li
                        key={item.id}
                        className="flex justify-between text-base text-gray-700"
                      >
                        <span>{item.name}</span>
                        <span className="text-right">
                          <span className="block text-xs text-gray-500">
                            Qty: {item.quantity} × {getCurrencySymbol()}
                            {unit.toFixed(2)}
                          </span>
                          <span className="font-medium">
                            {getCurrencySymbol()}
                            {lineTotal.toFixed(2)}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="mt-2 text-gray-600 text-left">
                  No items in the basket yet.
                </p>
              )}
            </div>

            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
              {/* Subtotal */}
              <div className="col-span-full">
                <label
                  htmlFor="subtotal"
                  className="block text-left text-sm font-medium text-gray-400"
                >
                  Subtotal
                </label>
                <input
                  type="text"
                  name="subtotal"
                  id="subtotal"
                  value={`${getCurrencySymbol()} ${subtotal.toFixed(2)}`}
                  className="mt-2 block w-full border-b border-gray-900/10 text-lg text-gray-900 bg-transparent sm:text-lg focus:outline-none"
                  readOnly
                />
              </div>

              {/* Shipping Address */}
              <ShippingAddress address={address} setAddress={setAddress} />

              {/* Minimum Order Adjustment */}
              {minimumOrderAdjustment > 0 && (
                <div className="col-span-full">
                  <label
                    htmlFor="minimum-order"
                    className="block text-left text-sm font-medium text-gray-400"
                  >
                    Minimum Order Adjustment
                  </label>
                  <input
                    type="text"
                    id="minimum-order"
                    value={`${getCurrencySymbol()} ${minimumOrderAdjustment.toFixed(
                      2
                    )}`}
                    className="mt-2 block w-full border-b border-gray-900/10 py-1.5 text-lg text-gray-900 bg-transparent"
                    readOnly
                  />
                </div>
              )}

              {/* VAT */}
              <div className="col-span-full">
                <label
                  htmlFor="vat"
                  className="block text-left text-sm font-medium text-gray-400"
                >
                  GST (18%)
                </label>
                <input
                  type="text"
                  name="vat"
                  id="vat"
                  value={`${getCurrencySymbol()} ${vat.toFixed(2)}`}
                  className="mt-2 block w-full border-b border-gray-900/10 py-1.5 text-lg text-gray-900 bg-transparent focus:outline-none"
                  readOnly
                />
              </div>

              {/* Total */}
              <div className="col-span-full">
                <label
                  htmlFor="total"
                  className="block text-left text-sm font-medium text-gray-400"
                >
                  Total
                </label>
                <input
                  type="text"
                  name="total"
                  id="total"
                  value={`${getCurrencySymbol()} ${total.toFixed(2)}`}
                  className="mt-2 block w-full border-b border-gray-900/10 py-1.5 text-3xl font-bold text-gray-900 bg-transparent focus:outline-none"
                  readOnly
                />
              </div>

              {/* Minimum Order Warning */}
              {isBelowMinimum && (
                <div className="col-span-full">
                  <p className="text-red-600 text-sm font-medium mt-2">
                    Minimum order is {getCurrencySymbol()}{" "}
                    {requiredMinimum.toFixed(2)}.
                  </p>
                </div>
              )}

              {/* Proceed to Checkout */}
              <div className="col-span-full mt-4">
                <button
                  type="button"
                  className={`w-full px-4 py-2 text-white font-semibold rounded ${
                    isBelowMinimum
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                  onClick={handleProceedToCheckout}
                  disabled={isBelowMinimum}
                >
                  Proceed to checkout
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Large order modal */}
      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <h2 className="text-xl font-bold">Large Order Detected</h2>
          <p className="mt-4">
            Your order total is {getCurrencySymbol()} {total.toFixed(2)}. For
            orders above {getCurrencySymbol()} 100,000, please contact us for
            manual assistance to ensure a smooth transaction.
          </p>

          <div className="mt-6 text-center">
            <p className="font-semibold text-gray-700">Contact Details:</p>
            <p className="mt-2">
              Email:{" "}
              <a
                href={`mailto:info@supersprings.co.in?subject=Large Order Inquiry&body=Hello, I am inquiring about a large order from your website. My basket total is ${getCurrencySymbol()} ${total.toFixed(
                  2
                )}. Please let me know the next steps.`}
                className="text-blue-600 hover:underline"
              >
                info@supersprings.co.in
              </a>
            </p>
            <p>
              Phone:{" "}
              <a
                href="tel:+919810460594"
                className="text-blue-600 hover:underline"
              >
                +91 98104 60594
              </a>
            </p>
          </div>

          <button
            onClick={() => setShowModal(false)}
            className="mt-6 w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            OK
          </button>
        </Modal>
      )}
    </>
  );
};

export default BasketTotals;